import { useEffect, useRef } from 'react';
import cnh from '~/helpers/cnh';
import styles from '~/styles/alert.module.css';
import { LaravelValidationErrors } from '~/types/form';
import IconAlertTriangle from '~icons/tabler/alert-triangle';

type AlertError = {
  message?: string;
  validationErrors?: LaravelValidationErrors | null;
};

type Props = {
  icon?: string;
  message?: string;
  children?: React.ReactNode;
  error?: AlertError | null;
  type?: 'danger' | 'info' | 'success';
  focusOnRender?: boolean;
  className?: string;
};

export default function Alert({
  icon,
  message,
  children,
  error,
  type = 'danger',
  focusOnRender = false,
  className,
}: Props) {
  const alertRef = useRef<HTMLDivElement>(null);

  const classVariant = {
    danger: styles.alertDanger,
    info: styles.alertInfo,
    success: styles.alertSuccess,
  };

  useEffect(() => {
    if (focusOnRender) {
      const offsetTop = alertRef.current?.offsetTop;

      if (offsetTop) {
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });
      }
    }
  }, [focusOnRender, message, children]);

  if (!error && !message && !children) {
    return null;
  }

  if (typeof error === 'object' && error !== null) {
    if ('validationErrors' in error && error.validationErrors) {
      return (
        <div className={cnh(styles.alert, classVariant[type])} ref={alertRef} role="alert">
          <ul>
            {Object.entries(error.validationErrors).map(([field, errors]) => (
              <li key={field}>{errors.join(', ')}</li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <div className={cnh(styles.alert, classVariant[type])} ref={alertRef} role="alert">
        {error.message}
      </div>
    );
  }

  return (
    <div
      className={cnh(styles.alert, classVariant[type], className)}
      ref={alertRef}
      role="alert"
    >
      {icon && icon === 'alert-triangle' && (
        <IconAlertTriangle className={styles.alertIcon} />
      )}
      {message}
      {children}
    </div>
  );
}
